import React from 'react';
import { 
  ExclamationTriangleIcon, 
  SparklesIcon 
} from '@heroicons/react/24/outline';
import PaymentModal from '@/components/payments/PaymentModal';
import Modal from './Modal';
import { usePlanes } from '@/hooks/usePlanes';
import { useSimpleEmpresa } from '@/hooks/useSimpleEmpresa';

/**
 * Modal que se muestra cuando el trial expira
 * Bloquea el acceso hasta que el usuario actualice su plan
 */
const TrialExpiredModal = ({ isOpen, onClose }) => {
  const { planes } = usePlanes();
  const { empresaActual } = useSimpleEmpresa();
  const [showPaymentModal, setShowPaymentModal] = React.useState(false);

  // Encontrar el primer plan pagado
  const firstPaidPlan = planes.find(p => p.price > 0);

  const handleUpgradeClick = () => {
    setShowPaymentModal(true);
  };

  const handleClosePaymentModal = () => {
    setShowPaymentModal(false);
    // Cuando se cierre el modal de pago, el sistema debería verificar
    // automáticamente si el plan cambió
  };

  const footer = (
    <>
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <button
          onClick={handleUpgradeClick}
          className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold py-3 px-4 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2"
        >
          <SparklesIcon className="h-5 w-5" />
          <span>Actualizar Plan</span>
        </button>
      </div>
      
      {/* Nota legal */}
      <p className="text-xs text-gray-500 text-center">
        Tus datos están seguros y se mantendrán por 30 días adicionales
      </p>
    </>
  );

  const title = (
    <div className="text-center">
      <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
        <ExclamationTriangleIcon className="h-8 w-8 text-red-600" />
      </div>
      <h2 className="text-2xl font-bold text-white mb-2">
        ¡Trial Expirado!
      </h2>
    </div>
  );

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={title}
        size="lg"
        closeOnEsc={false}
        closeOnOverlayClick={false}
        showCloseButton={true}
        footer={footer}
      >
        <div className="text-center mb-6">
          <p className="text-gray-300">
            Tu período de prueba de 30 días ha terminado. Para continuar usando Revvly, 
            necesitas actualizar a un plan de pago.
          </p>
        </div>

        {/* Información de la empresa */}
        <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 mb-6">
          <h3 className="text-red-400 font-semibold mb-2">Empresa: {empresaActual?.nombre}</h3>
          <p className="text-gray-300 text-sm">
            Registrada el: {new Date(empresaActual?.fecha_registro).toLocaleDateString('es-CL')}
          </p>
        </div>

        {/* Beneficios de actualizar */}
        <div>
          <h3 className="text-white font-semibold mb-3">¿Por qué actualizar?</h3>
          <ul className="space-y-2 text-sm text-gray-300">
            <li className="flex items-center">
              <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
              Acceso ilimitado a todas las funciones
            </li>
            <li className="flex items-center">
              <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
              Soporte técnico prioritario
            </li>
            <li className="flex items-center">
              <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
              Respaldos automáticos de tus datos
            </li>
            <li className="flex items-center">
              <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
              Actualizaciones y nuevas funciones
            </li>
          </ul>
        </div>
      </Modal>

      {/* Modal de pago */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={handleClosePaymentModal}
        targetPlanId={firstPaidPlan?.id}
      />
    </>
  );
};

export default TrialExpiredModal;